import { Button, Stack, Box, Checkbox } from "@mui/material";
import React, { useEffect, useState } from "react";
import { tease } from "../components/CheckButton";
import { GoogleLogin, useGoogleLogin } from "@react-oauth/google";
import {
  ActionType,
  Color,
  ContactType,
  ErrorCode,
  FontStyle,
  navPaddingTop,
  Slug,
  SurveyButtonStyle,
} from "../constants";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { StyledTextField } from "../components/AutocompleteUtils";
import * as apiService from "../service/ApiService";
import { useDispatch, useSelector } from "react-redux";
import googleIcon from "../img/googleIcon.png";
import { useNavigate } from "react-router-dom";

const styles = {
  outer: {
    paddingTop: navPaddingTop,
  },
  container: {
    // marginTop: '67px',
    paddingTop: "3rem",
    paddingBottom: "110px",
    px: 4,
    backgroundColor: "#fff",
    height: "100%",
    display: "flex",
    alignItems: "stretch",
    minHeight: "calc(100vh - 67px)",
  },
  inner: {
    width: "100%",
    maxWidth: "24.38rem",
    mx: "auto",
    textAlign: "center",
  },
  heading: {
    ...FontStyle.title,
    fontSize: "1.73rem",
  },
  panel: {
    width: "100%",
    // backgroundColor: '#fff',
    // borderRadius: '0.75rem',
    // padding: '1.2rem',
    "& .MuiFormControl-root": {
      border: "none",
    },
  },
  label: {
    ...FontStyle.headingBody,
    fontSize: "1.2rem",
  },
  search: {
    width: "100%",
    maxWidth: "500px",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "4px",
    border: "1px solid rgb(204, 204, 204)",
  },
  button: {
    ...FontStyle.headingBody,
    width: "100%",
    fontSize: "1rem",
    textTransform: "none",
    color: "black",
    borderRadius: "6.25rem",
    padding: "0.4rem 2rem",
    backgroundColor: Color.primary,
    border: `1px solid ${Color.primary}`,
    "&:hover": {
      backgroundColor: Color.primaryDark,
    },
    transition: "all 0.3s ease-in-out 0s",
  },
  buttonWhite: {
    backgroundColor: "white",
    border: "none",
    "&:hover": {
      backgroundColor: "rgb(252,252,252)",
    },
  },
  link: {
    ...FontStyle.headingBody,
    width: "100%",
    textAlign: "center",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  forgotPassword: {
    ...FontStyle.body,
    width: "100%",
    textAlign: "left",
    textDecoration: "underline",
    cursor: "pointer",
    "&:hover": {
      color: "green",
    },
    // fontSize: "14px",
  },
  noAccount: {
    ...FontStyle.headingBody,
    textAlign: "center",
  },
  body: {
    ...FontStyle.body,
    fontSize: "1rem",
    textAlign: "center",
    lineHeight: "1.3",
  },
  orText: {
    ...FontStyle.body,
    fontSize: "0.83rem",
    lineHeight: "1.3",
    textAlign: "inherit",
    color: "rgb(160, 160, 160)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  bar: {
    width: "40%",
    height: "2px",
    backgroundColor: "rgb(160, 160, 160)",
  },
  googleCustomButton: {
    textTransform: "none",
    fontFamily: '"Google Sans",arial,sans-serif',
    fontWeight: "500",
    height: "46px",
    width: "100%",
    border: "1px solid #dadce0",
    color: "#202124",
    "&:hover": {
      border: "1px solid #d2e3fc",
    },
  },
  error: {
    ...FontStyle.body,
    color: "red",
    fontSize: "0.75rem",
    textAlign: "left",
    width: "100%",
  },
  success: {
    ...FontStyle.body,
    color: "green",
    fontSize: "16px",
    textAlign: "center",
    width: "100%",
  },
};

const LabelInputStack = ({ label, children }) => {
  return (
    <Stack
      direction="column"
      spacing="0.5rem"
      sx={{
        width: "100%",
      }}
      alignItems="flex-start">
      <Box sx={{ ...styles.label, fontSize: "1rem" }}>{label}</Box>
      {children}
    </Stack>
  );
};

const ResetPassword = () => {
  const queryParameters = new URLSearchParams(window.location.search);
  const token = queryParameters.get("token");
  const email = queryParameters.get("email");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [clicked, setClicked] = React.useState(false);
  const [error, setError] = useState("");
  const handleSend = () => {
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    } else if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    dispatch({ type: ActionType.error, payload: { error_code: null } });
    apiService.resetPassword(
      dispatch,
      {
        contactInfo: email,
        token,
        newPassword: password,
      },
      () => {
        setClicked(true);
      }
    );
  };

  return (
    <Box sx={styles.outer}>
      <Box sx={styles.container}>
        <Stack
          sx={styles.inner}
          direction="column"
          alignItems="center"
          spacing="1rem">
          <Box sx={styles.heading}>Set a new password</Box>
          {!clicked && (
            <>
              <Stack
                sx={styles.panel}
                direction="column"
                alignItems="center"
                spacing="1rem">
                <LabelInputStack label="Password">
                  <StyledTextField
                    InputProps={{ disableUnderline: true }}
                    sx={styles.search}
                    variant="standard"
                    type="password"
                    placeholder=""
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </LabelInputStack>
                <LabelInputStack label="Confirm Password">
                  <StyledTextField
                    InputProps={{ disableUnderline: true }}
                    sx={styles.search}
                    variant="standard"
                    type="password"
                    placeholder=""
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </LabelInputStack>
                {auth.error && (
                  <Box sx={styles.error}>
                    {auth.error === ErrorCode.tokenExpired
                      ? "Token expired"
                      : "Password reset failed"}
                  </Box>
                )}
                {auth.uncaughtError && (
                  <Box sx={styles.error}>An error occured</Box>
                )}
                {error && <Box sx={styles.error}>{error}</Box>}
              </Stack>

              <Button
                sx={{
                  ...SurveyButtonStyle.defaultStyling,
                  ...SurveyButtonStyle.primaryColorScale,
                }}
                disableRipple
                fullWidth
                onClick={handleSend}>
                Reset Password
              </Button>
            </>
          )}
          {clicked && (
            <>
              <Box sx={styles.success}>Your password has been reset!</Box>
              <Button
                sx={{
                  ...SurveyButtonStyle.defaultStyling,
                  ...SurveyButtonStyle.primaryColorScale,
                }}
                disableRipple
                fullWidth
                onClick={() => navigate(Slug.login)}>
                Back to Login
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default ResetPassword;
